import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSEO } from '@hooks/useSEO';
import { HomeContainer } from '@pages/Home/styles';
import { EditaisParticipantes } from '@libs/types';
import { CardEdital } from '@components/CardEdital';
import { Backdrop, Dialog, DialogContent } from '@mui/material';
import { Paginator } from '@components/Paginator';
import { TextInput } from '@components/TextInput';
import { Select } from '@components/Select';
import { states } from '@utils/states';
import { editaisEmptyMock } from '../../utils/editaisMock';
import { Header } from '@components/Header';
import { Spin } from 'antd';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import useMediaQuery from '@mui/material/useMediaQuery';
import { dadosSuporteMock } from '@utils/dadosSuporteMock';
import Slider from '@assets/slider.svg';
import ReCAPTCHA from 'react-google-recaptcha';
import MailchimpPopup from '@components/MailchimpPopup';
import { Banner } from '@components/PreEditalModal/Banner';
import bannerEdital from '@assets/homem-escritorio-banner.png';
import bannerGov from '@assets/mulher-escritorio-banner.png';
import bannerEditalMobile from '@assets/homem-escritorio-banner-mobile.png';
import bannerGovMobile from '@assets/mulher-escritorio-banner-mobile.png';
import logoBBMNET from '@assets/logobbmnet.png';
import logojoinsy from '@assets/logojoinsy.png';
import logoAdvtech from '@assets/logoAdvTechPro.svg';
import logoJoinsyBlack from '@assets/joinsyBlack.png';
import { AsasFormModal } from '@components/AsasModal';
import logoAsasGarantia from '@assets/logoAsasGarantias.svg';
import {
  getEditais,
  getModalidades,
  getOrgaosPromotores,
} from '../../api/EditaisService';
import type { OrgaoOption } from '@libs/types';
import {
  buildEditaisQueryString,
  buildOrgaoSuggestions,
  buildSituacaoOptions,
  selectedOrgao as resolveOrgaoPromotorId,
  type SearchState,
  type QueryFeatureFlags,
} from './orgaoFilter.utils';

/**
 * Feature flags por filtro dependente do backend do jornal (task_01). Default
 * habilitado — só a string 'false' explícita (runtime `config.js` ou `.env`)
 * desliga o filtro. Lida do config de runtime, com fallback para `process.env`.
 */
const readFlag = (key: string): boolean => {
  const win =
    typeof window !== 'undefined'
      ? ((window.__APP_CONFIG__ ?? window.__RUNTIME_CONFIG__) as
          | Record<string, unknown>
          | undefined)
      : undefined;
  const raw = win?.[key] ?? process.env[key];
  return String(raw ?? '').toLowerCase() !== 'false';
};

export type PaginationType = {
  take: number;
  skip: number;
};

export type Option = {
  label: string;
  value: string;
  disabled?: boolean;
};

type SelectOptions = {
  estado: Option[];
  modalidade: Option[];
  orgaoPromotor: Option[];
  situacoes: Option[];
  objeto: Option[];
  segmento: Option[];
  cidades: Option[];
};

const PAGINATION: PaginationType = {
  take: 10,
  skip: 0,
};

// Lista estática de modalidades — fallback enquanto a rota
// `api/Editais/Modalidades` não está exposta (ver getModalidades). Quando o
// serviço responder, o retorno real substitui esta lista.
const MODALIDADES_FALLBACK: Option[] = [
  { label: 'Concorrência', value: '1' },
  { label: 'Pregão (Setor público)', value: '3' },
  { label: 'Dispensa', value: '5' },
  { label: 'Credenciamento', value: '6' },
  { label: 'Tecnica e Preço', value: '7' },
];

interface PreEditalModalProps {
  open: boolean;
  onClose: () => void;
  onNavigateToHome: () => void;
}

export const PreEditalModal = ({
  open,
  onClose,
  onNavigateToHome,
}: PreEditalModalProps) => {
  const isMobile = useMediaQuery('(max-width:768px)');

  // Função para rastrear cliques no primeiro botão
  const handleFirstButtonClick = () => {
    // Envia evento para GTM
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'click',
        event_category: 'PreEditalModal',
        event_label: 'Filtre as oportunidades',
        value: 1,
      });
    }
    onNavigateToHome();
    onClose();
  };

  // Função para rastrear cliques no segundo botão
  const handleSecondButtonClick = () => {
    // Envia evento para GTM
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'click',
        event_category: 'PreEditalModal',
        event_label: 'Conecte-se com compradores',
        value: 1,
      });
    }
    window.open(
      'https://conteudo.bbmnet.com.br/bbmnet-joinsy?utm_source=BBM&utm_medium=Journal&utm_campaign=Site',
      '_blank',
    );
  };

  return (
    <>
      <Backdrop
        open={open}
        sx={{
          backdropFilter: 'blur(8px)',
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          zIndex: 1200,
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
      />

      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="lg"
        sx={{
          '& .MuiDialog-container': {
            zIndex: 1300,
          },
        }}
        PaperProps={{
          style: {
            borderRadius: '16px',
            backgroundColor: 'transparent',
            boxShadow: 'none',
            overflow: 'hidden',
            margin: isMobile ? '16px' : '32px',
            maxHeight: isMobile ? '90vh' : 'none',
            border: 'none',
          },
        }}
      >
        <DialogContent
          style={{
            padding: 0,
            overflowX: 'hidden',
            display: 'flex',
            justifyContent: 'center',
            backgroundColor: 'transparent',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? '20px' : '32px',
              padding: isMobile ? '0' : '16px',
              width: '100%',
              maxWidth: '1200px',
              overflowY: 'auto',
            }}
          >
            <Banner
              image={isMobile ? bannerEditalMobile : bannerEdital}
              title={`Acesse aqui todos os Editais\npublicados no BBMNET`}
              buttonText="Filtre as oportunidades de seu interesse"
              description="Todas as publicações sem custo e sem cadastro prévio"
              onButtonClick={handleFirstButtonClick}
              isMobile={isMobile}
              overlayImage={logoBBMNET}
              titleMargin={isMobile ? '16px' : '20px'}
              overlayImageStyle={{
                ...(!isMobile && {
                  width: '100px',
                  top: '40px',
                  left: '40px',
                }),
              }}
              style={{
                flex: isMobile ? undefined : 1,
                margin: isMobile ? '0 auto' : undefined,
                maxWidth: isMobile ? '100%' : undefined,
              }}
            />

            <Banner
              image={isMobile ? bannerGovMobile : bannerGov}
              title={`Quer aumentar sua\nprodutividade nas vendas\npara o Governo?`}
              buttonText="Conecte-se com compradores públicos e privados e aumente suas vendas"
              description="Maximize suas vendas com as soluções Joinsy"
              isMobile={isMobile}
              isSecondBanner={true}
              contentBackground="#121212"
              customGradient="linear-gradient(to bottom, rgba(18, 18, 18, 0) 0%, rgba(18, 18, 18, 1) 100%)"
              titleColor="#C3D200"
              titleMargin={isMobile ? '0' : '-20px'}
              overlayImage={logojoinsy}
              onButtonClick={handleSecondButtonClick}
              overlayImageStyle={{
                ...(!isMobile && {
                  width: '140px',
                  top: '40px',
                  left: '40px',
                  filter: 'drop-shadow(0px 4px 6px rgba(0, 0, 0, 0.4))',
                }),
              }}
              style={{
                flex: isMobile ? undefined : 1,
                margin: isMobile ? '0 auto' : undefined,
                maxWidth: isMobile ? '100%' : undefined,
              }}
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const Home = ({ edital }) => {
  useSEO({
    title: 'Jornal do Licitante | Editais de Licitação Públicos | BBMNET',
    description:
      'Acesse todos os editais de licitação publicados no BBMNET. Pregão, Concorrência, Dispensa e mais — filtre por estado, modalidade, órgão e palavra-chave. Gratuito e sem cadastro.',
    canonical: 'https://jornaldolicitante.com.br/',
  });

  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [participantes, setParticipantes] =
    useState<EditaisParticipantes | null>(null);
  const [selectOptions, setSelectOptions] = useState<SelectOptions>({
    estado: states,
    situacoes: [],
  } as SelectOptions);
  const [search, setSearch] = useState({
    numeroEdital: '',
    estado: '',
    modalidade: '',
    orgao: '',
    orgaoPromotorId: '',
    situacao: '',
    palavraChave: '',
    objeto: '',
    segmento: '',
    cidade: '',
  });
  const [orgaoOptions, setOrgaoOptions] = useState<OrgaoOption[]>([]);
  // Situação: lista estável espelhando `StatusSalaDisputa` (ADR-007).
  const situacaoSelectOptions: Option[] = buildSituacaoOptions().map((o) => ({
    label: o.label,
    value: String(o.value),
  }));
  const featureFlags: QueryFeatureFlags = {
    palavraChave: readFlag('FEATURE_PALAVRA_CHAVE'),
    segmento: readFlag('FEATURE_SEGMENTO'),
  };
  const orgaoDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isDivTwoVisible, setIsDivTwoVisible] = useState(false);
  const isSmallScreen = useMediaQuery('(max-width: 768px)');
  const [filterText, setFilterText] = useState('');
  const [selectedOrgao, setSelectedOrgao] = useState<any>();
  const [showMailchimpPopup, setShowMailchimpPopup] = useState(false);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [showPreEditalModal, setShowPreEditalModal] = useState(false);
  const [showAsasFormModal, setShowAsasFormModal] = useState(false);
  const SITE_KEY = process.env.JORNAL_RECAPTCHA_SITE_KEY as string;

  const hasActiveFilters =
    Boolean(search.modalidade) ||
    Boolean(search.numeroEdital?.trim()) ||
    Boolean(search.objeto?.trim()) ||
    Boolean(search.estado?.trim()) ||
    Boolean(search.situacao?.trim()) ||
    Boolean(search.palavraChave?.trim()) ||
    Boolean(search.segmento?.trim()) ||
    Boolean(search.orgao?.trim());

  const getTotalPages = (response: any, take: number) => {
    if (typeof response?.totalPages === 'number') {
      return response.totalPages || 1;
    }

    const totalElements =
      response?.totalElements ??
      response?.count ??
      response?.total ??
      response?.content?.length ??
      0;

    return Math.max(1, Math.ceil(totalElements / take));
  };

  const fetchEditais = useCallback(
    (skip: number, take: number) => {
      // Resolve o nome do órgão selecionado para OrgaoPromotorId (Guid) e monta
      // a query do jornal (PascalCase, paginação server-side) via utils testável.
      const { orgaoPromotorId } = resolveOrgaoPromotorId(
        search as SearchState,
        orgaoOptions,
      );
      const queryState: SearchState = {
        ...(search as SearchState),
        orgaoPromotorId: orgaoPromotorId ?? '',
      };
      const queryString = buildEditaisQueryString(
        queryState,
        // `skip` aqui é o ÍNDICE da página (0-based); o backend espera o OFFSET
        // de registros, então convertemos multiplicando pelo tamanho da página.
        { skip: skip * take, take },
        featureFlags,
      );

      setLoading(true);

      getEditais(queryString)
        .then((result) => {
          const sortedEditais = [...result.content].sort((a, b) => {
            const dateA = a.publishedAt ? new Date(a.publishedAt).getTime() : 0;
            const dateB = b.publishedAt ? new Date(b.publishedAt).getTime() : 0;
            return dateB - dateA;
          });

          const totalPages =
            hasActiveFilters && sortedEditais.length < take && skip === 0
              ? 1
              : getTotalPages(result, take);

          setParticipantes({
            editais: sortedEditais,
            count: totalPages,
          });
        })
        .catch((error) => {
          console.error('Erro na requisição:', error);
          setParticipantes(editaisEmptyMock);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [hasActiveFilters, search, orgaoOptions],
  );

  const toggleDivTwo = () => {
    setIsDivTwoVisible(!isDivTwoVisible);
  };

  const handleSelectOption = (value: string | number, name: string) => {
    setSearch({
      ...search,
      [name]: value,
    });
  };

  // Modalidades vêm do serviço (`GET api/Editais/Modalidades`), mapeadas para as
  // opções do Select ({ label: name, value: id }). Erro degrada para lista vazia.
  const fetchModalidades = useCallback(() => {
    getModalidades()
      .then((modalidades) => {
        const modalidadeOptions: Option[] = modalidades.map((modalidade) => ({
          label: modalidade.name,
          value: String(modalidade.id),
        }));

        setSelectOptions((prevState) => ({
          ...prevState,
          modalidade: modalidadeOptions,
        }));
      })
      .catch((error) => {
        console.error('Erro ao carregar modalidades:', error);
        // Fallback: mantém a lista estática enquanto a rota não está exposta.
        setSelectOptions((prevState) => ({
          ...prevState,
          modalidade: MODALIDADES_FALLBACK,
        }));
      });
  }, []);

  const handleGetSupport = () => {
    const { ramoAtividade } = dadosSuporteMock;

    if (ramoAtividade) {
      const objetoOptions = ramoAtividade.map((objeto: any) => ({
        label: objeto.descricao,
        value: objeto.uniqueId,
      }));

      setSelectOptions((prevState) => ({
        ...prevState,
        objeto: objetoOptions,
      }));
    }
  };

  const handleClear = () => {
    const clearButton = document.querySelector<HTMLElement>(
      '.MuiAutocomplete-clearIndicator',
    );
    if (clearButton) {
      clearButton.click();
    }
    setSearch({
      numeroEdital: '',
      estado: '',
      modalidade: '',
      orgao: '',
      orgaoPromotorId: '',
      situacao: '',
      palavraChave: '',
      objeto: '',
      segmento: '',
      cidade: '',
    });
    setSelectedOrgao(null);
    setOrgaoOptions([]);
    setCurrentPage(0);
  };

  // Filtro de cidade adiado (ADR-007): sem carga de municípios e sem envio de
  // nenhum parâmetro de cidade ao LICITACOES. Follow-up: tabela/rota no cadastro.

  const handleFilter = useCallback(
    (event?: React.FormEvent<HTMLFormElement>) => {
      event && event.preventDefault();
      setCurrentPage(0);
      fetchEditais(0, PAGINATION.take);
    },
    [fetchEditais],
  );

  const handleSignupClick = () => {
    setShowCaptcha(true);
    setShowMailchimpPopup(false);
  };

  const handleCaptchaChange = (token: string | null) => {
    if (token) {
      setToken(token);
      setShowCaptcha(false);
      setShowMailchimpPopup(true);
    } else {
      setShowCaptcha(false);
      setShowMailchimpPopup(false);
    }
  };

  const handleMailchimpClose = () => {
    setShowMailchimpPopup(false);
    setToken(null);
  };

  const handleOrgaoChange = (_event: React.SyntheticEvent, value: string) => {
    const nomeOrgao = typeof value === 'string' ? value : '';
    setSearch((prev) => ({ ...prev, orgao: nomeOrgao }));

    if (orgaoDebounceRef.current) clearTimeout(orgaoDebounceRef.current);

    if (!nomeOrgao.trim()) {
      setOrgaoOptions([]);
      return;
    }

    orgaoDebounceRef.current = setTimeout(() => {
      // Autocomplete pela rota exclusiva do jornal (`Jornal/OrgaosPromotores`).
      // Indisponível/erro → degrada para entrada simples, sem lançar.
      getOrgaosPromotores(nomeOrgao)
        .then((result) => setOrgaoOptions(buildOrgaoSuggestions(result)))
        .catch(() => setOrgaoOptions([]));
    }, 350);
  };

  useEffect(() => {
    handleGetSupport();
    fetchModalidades();
    fetchEditais(0, PAGINATION.take);

    // Situação vem da lista estável `StatusSalaDisputa` (ADR-007), sem endpoint.

    const timer = setTimeout(() => {
      setShowAsasFormModal(true);
    }, 0.5);

    return () => clearTimeout(timer);
  }, []);

  const handlePageChange = (page: PaginationType) => {
    if (page.skip >= (participantes?.count ?? 1)) return;

    setCurrentPage(page.skip);
    fetchEditais(page.skip, page.take);
  };

  const handleFilterTextChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setFilterText(event.target.value);
  };

  const borderRadiusStyle = {
    borderRadius: isDivTwoVisible
      ? '0px'
      : window.innerWidth < 900
        ? '10px'
        : '0px 0px 10px 10px',
    height: window.innerWidth < 900 ? '120%' : 'auto',
    padding: window.innerWidth < 900 ? '1.2em' : 'auto',
  };

  const handleFilterButtonClick = () => {
    setSearch({
      ...search,
      palavraChave: filterText,
    });
    handleFilter();
  };

  const scrollToFilters = () => {
    const filtersSection = document.getElementById('filters-section');
    if (filtersSection) {
      filtersSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleClosePreEditalModal = () => {
    setShowPreEditalModal(false);
    setTimeout(() => setShowAsasFormModal(true), 100);
  };

  useEffect(() => {
    console.log('[DEBUG] GTM Status:', {
      gtagExists: typeof window.gtag !== 'undefined',
      dataLayerExists: Array.isArray(window.dataLayer),
    });
  }, []);

  return (
    <HomeContainer>
      <Backdrop
        open={showPreEditalModal}
        sx={{
          backdropFilter: 'blur(8px)',
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          zIndex: 1200, // Valor alto para ficar sobre outros elementos
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
      />

      <Header />
      <section>
        <header>
          <div className="titles">
            <h4 className="only-mobile">Jornal do Licitante</h4>
            <h6 className="only-mobile">Editais Publicados</h6>
            <h4 className="only-desktop">Editais Publicados</h4>
          </div>
          {!isSmallScreen && (
            <div className="newsletter">
              <span>Quer receber os Editais por e-mail?</span>
              {showCaptcha ? (
                <div className="captcha-container">
                  <ReCAPTCHA
                    key={Math.random()}
                    sitekey={SITE_KEY}
                    onChange={handleCaptchaChange}
                  />
                </div>
              ) : (
                <button
                  className="register"
                  style={{ width: '100%' }}
                  onClick={handleSignupClick}
                >
                  Cadastre aqui
                </button>
              )}
              {showMailchimpPopup && (
                <MailchimpPopup onClose={handleMailchimpClose} />
              )}
            </div>
          )}
        </header>
      </section>
      <main>
        <form
          style={{
            display: 'contents',
            width: 'inherit',
            backgroundColor: '#f8f4f4',
          }}
          onSubmit={handleFilter}
          id="filters-section"
        >
          {window.innerWidth > 900 && (
            <div
              className="divextra"
              style={{
                backgroundColor: '#004aa0',
                width: '100%',
                paddingTop: '10px',
                borderRadius: '12px 12px 0 0',
              }}
            >
              {' '}
              <span
                style={{
                  color: 'white',
                  marginLeft: '2.5em',
                  fontSize: '26px',
                  fontWeight: 'bold',
                }}
              >
                Filtros
              </span>
            </div>
          )}
          <div className="divone" style={borderRadiusStyle}>
            <TextInput
              onChange={(event) =>
                handleSelectOption(event.currentTarget.value, 'palavraChave')
              }
              value={search.palavraChave}
              label={
                window.innerWidth > 900 ? (
                  <span
                    style={{
                      color: 'white',
                      paddingLeft: '5em',
                      paddingBottom: '10px',
                    }}
                  >
                    Buscar
                  </span>
                ) : (
                  <span />
                )
              }
              placeholder="Palavra Chave"
              style={{
                width: window.innerWidth < 900 ? '100% !important' : '40em',
                alignSelf: 'center',
                height: window.innerWidth < 900 ? '2.5em' : '2.5em',
                borderRadius: '4px',
                borderColor: 'white',
                marginRight: '10px',
                marginLeft: '5em',
                paddingLeft: '2em',
                marginBottom: '26px',
                marginTop: '10px',
                fontSize: window.innerWidth < 900 ? '20px' : '18px',
                color: 'black',
                boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
              }}
              className="palavra-chave"
            />
            <div className="blue">
              {window.innerWidth > 900 && (
                <button
                  className="filter-button"
                  type="submit"
                  style={{
                    fontSize: '20px',
                    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
                    borderRadius: '4px',
                    width: '6em',
                    height: '2em',
                    margin: '0',
                  }}
                >
                  Filtrar
                </button>
              )}
              {window.innerWidth > 900 && (
                <button
                  className="filter-button"
                  style={{
                    fontSize: '20px',
                    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
                    borderRadius: '4px',
                    width: '6em',
                    height: '2em',
                    margin:
                      window.innerWidth < 900 ? '0.5em' : '0 0.5em 0 0.5em',
                  }}
                  onClick={handleClear}
                >
                  Limpar
                </button>
              )}
            </div>

            {window.innerWidth <= 900 && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  margin: '1em',
                }}
              >
                <button
                  className="filter-button"
                  type="submit"
                  style={{
                    fontSize: '20px',
                    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
                    borderRadius: '30px',
                    width: '6em',
                    height: '2em',
                    margin: '0',
                    alignSelf: 'center',
                    justifySelf: 'center',
                  }}
                >
                  Filtrar
                </button>
                <button
                  className="filter-button"
                  style={{
                    fontSize: '20px',
                    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
                    borderRadius: '30px',
                    width: '6em',
                    height: '2em',
                    margin:
                      window.innerWidth < 900 ? '0.5em' : '0 0.5em 0 0.5em',
                  }}
                  onClick={handleClear}
                >
                  Limpar
                </button>

                <div>
                  <img
                    className="slider"
                    src={Slider}
                    alt=""
                    width={32}
                    style={{ filter: 'invert(100%)' }}
                  />
                  <button
                    className="mais-filtros-button"
                    onClick={toggleDivTwo}
                    style={{
                      marginLeft: window.innerWidth < 900 ? '10px' : '4em',
                      padding:
                        window.innerWidth < 900
                          ? '0px 0px 0px 10px'
                          : '5px 10px',
                      fontSize: '22px',
                    }}
                  >
                    Mais filtros
                  </button>
                </div>
              </div>
            )}

            {window.innerWidth > 900 && (
              <div className="mais-filtros-div">
                <img
                  className="slider"
                  src={Slider}
                  alt=""
                  width={32}
                  style={{ filter: 'invert(100%)' }}
                />
                <button
                  className="mais-filtros-button"
                  onClick={toggleDivTwo}
                  style={{
                    marginLeft: window.innerWidth < 900 ? '0em' : '4em',
                    padding: window.innerWidth < 900 ? '0px' : '5px 10px',
                    fontSize: '22px',
                  }}
                >
                  Mais filtros
                </button>
              </div>
            )}
          </div>

          {isDivTwoVisible && (
            <div
              className="divtwo"
              style={{
                alignItems: window.innerWidth < 900 ? 'start' : 'auto',
              }}
            >
              <Autocomplete
                options={orgaoOptions}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                onInputChange={handleOrgaoChange}
                value={selectedOrgao}
                noOptionsText={
                  <div
                    style={{
                      fontSize: '16px',
                      fontFamily: 'Montserrat',
                      fontWeight: '400',
                    }}
                  >
                    Nenhum Resultado
                  </div>
                }
                onChange={(event, newValue) => {
                  setSelectedOrgao(newValue);
                  setSearch((prev) => ({
                    ...prev,
                    orgao: newValue?.label ?? '',
                  }));
                }}
                renderOption={(props, option) => (
                  <li
                    {...props}
                    style={{
                      fontSize: '16px',
                      padding: '12px 16px',
                      fontFamily: 'Montserrat',
                      fontWeight: '400',
                    }}
                  >
                    {option.label}
                  </li>
                )}
                ListboxProps={{
                  style: {
                    fontSize: '16px',
                    maxHeight: '400px',
                    backgroundColor: 'white',
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    className="orgao-input"
                    placeholder="Órgão"
                    variant="outlined"
                    sx={{
                      '& .MuiInputBase-root.MuiOutlinedInput-root.MuiInputBase-colorPrimary.MuiInputBase-fullWidth.MuiInputBase-formControl.MuiInputBase-adornedEnd.MuiAutocomplete-inputRoot':
                        {
                          padding: '0',
                        },
                      '& .MuiInputBase-input': {
                        color: 'black',
                        fontFamily: 'Montserrat',
                        fontSize: '2.2em',
                        fontWeight: '500',
                        marginRight: '0em',
                        backgroundColor: 'white',
                        padding: '0',
                        borderRadius: '4px',
                        textAlign: 'center',
                        '@media (max-width: 900px)': {
                          width: '230px !important',
                          height: '31px !important',
                        },
                      },
                      '& .MuiInputBase-root': {
                        padding: '0',
                        backgroundColor: 'white',
                        borderRadius: '4px',
                        width: '230px',
                        '@media (max-width: 900px)': {
                          width: '70% !important',
                          height: '31px !important',
                        },
                      },
                      '& .MuiAutocomplete-hasPopupIcon': {
                        padding: '0',
                      },
                      '& .MuiAutocomplete-input': {
                        padding: '0',
                        '@media (max-width: 900px)': {
                          width: '70% !important',
                          height: '31px !important',
                        },
                      },
                      '& .MuiSvgIcon-root': {
                        color: 'transparent',
                      },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          padding: '0',
                          width: '100%',
                          color: 'transparent',
                          borderColor: 'transparent',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'black',
                        fontSize: '3rem',
                        fontFamily: 'Montserrat',
                      },
                      '& .MuiButtonBase-root': {
                        marginRight: '1.5em',
                      },
                    }}
                  />
                )}
              />
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignContent: 'center',
                }}
              >
                {' '}
                <TextField
                  className="numero-input"
                  onChange={(event) =>
                    handleSelectOption(
                      event.currentTarget.value,
                      'numeroEdital',
                    )
                  }
                  value={search.numeroEdital}
                  placeholder="Nº. Edital"
                  variant="outlined"
                  sx={{
                    '& .MuiInputBase-root.MuiOutlinedInput-root.MuiInputBase-colorPrimary.MuiInputBase-fullWidth.MuiInputBase-formControl':
                      {
                        padding: '0',
                      },
                    '& .MuiInputBase-input': {
                      color: 'black',
                      fontFamily: 'Montserrat',
                      fontSize: '2.2em',
                      fontWeight: '500',
                      marginRight: '0em',
                      backgroundColor: 'white',
                      padding: '0',
                      borderRadius: '4px',
                      textAlign: 'center',
                      '@media (max-width: 900px)': {
                        width: '230px !important',
                        height: '31px !important',
                      },
                    },
                    '& .MuiInputBase-root': {
                      padding: '9px',
                      backgroundColor: 'white',
                      borderRadius: '4px',
                      width: '230px',
                      '@media (max-width: 900px)': {
                        width: '70% !important',
                        height: '31px !important',
                      },
                    },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        padding: '0',
                        width: '100%',
                        color: 'transparent',
                        borderColor: 'transparent',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'black',
                      fontSize: '3rem',
                      fontFamily: 'Montserrat',
                    },
                  }}
                />
              </div>
              <div className="selector">
                <Select
                  label=""
                  onChange={(value) => handleSelectOption(value, 'modalidade')}
                  value={search.modalidade}
                  options={selectOptions?.modalidade ?? []}
                  placeholder="Modalidade"
                  style={{ width: '230px' }}
                />
              </div>
              <div className="selector">
                <Select
                  label=""
                  onChange={(value) => handleSelectOption(value, 'situacao')}
                  value={search.situacao}
                  options={situacaoSelectOptions}
                  placeholder="Situação"
                  style={{ width: '230px' }}
                />
              </div>
              <div className="selector">
                <Select
                  label=""
                  onChange={(value) => handleSelectOption(value, 'estado')}
                  value={search.estado}
                  options={selectOptions?.estado ?? []}
                  placeholder="Estado"
                  style={{ width: '230px' }}
                />
              </div>
              {/* Filtro de cidade oculto (adiado — ADR-007). */}
            </div>
          )}
        </form>
        <div
          className="caderno"
          style={{
            width: '100%',
            height: '10%',
            marginTop: '1em',
            marginBottom: '1em',
            justifyContent: 'left',
            marginLeft: '1em',
            backgroundColor: '#f8f4f4',
            boxSizing: 'border-box',
          }}
        >
          <span
            style={{
              color: '#084ca4',
              fontWeight: 'bold',
              fontFamily: 'Montserrat',
              fontSize: '2em',
              marginLeft: '2em',
            }}
          >
            {search.palavraChave && `${search.palavraChave} `}
          </span>
        </div>
        <div className="divthree">
          {loading ? (
            <div className="spin">
              <Spin size="large" />
            </div>
          ) : (
            <div className="card-list">
              {participantes?.editais && participantes?.editais.length > 0 ? (
                participantes?.editais.map((edital) => (
                  <CardEdital key={edital.uniqueId} edital={edital} />
                ))
              ) : (
                <div className="no-results">
                  <span
                    style={{
                      color: '#084ca4',
                      fontWeight: 'bold',
                      fontFamily: 'Montserrat',
                      fontSize: '2em',
                      marginLeft: '4em',
                      paddingBottom: '2em',
                    }}
                  >
                    Nenhum resultado encontrado
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
        <Paginator
          onPageChange={handlePageChange}
          currentPage={currentPage}
          totalPages={participantes?.count ?? 1}
          take={PAGINATION.take}
        />
        <div className="partners">
          <h6>Nossos parceiros</h6>
          <div className="logos">
            <a
              href="https://advtechpro.ai/?idAffiliate=$2a$10$zxZo8pg.4lrwBSzuPn/hie9wMoVCaa1st0nWfuZXyhj6IxFbhZ0je"
              target="_blank"
              rel="noreferrer"
            >
              <img src={logoAdvtech} alt="AdvTechPro.ai Logo" />
            </a>
            <a
              href="https://conteudo.bbmnet.com.br/bbmnet-joinsy?utm_source=BBM&utm_medium=Journal&utm_campaign=Site"
              target="_blank"
              rel="noreferrer"
            >
              <img src={logoJoinsyBlack} alt="Joinsy Logo" />
            </a>
            <button
              className="button-clean"
              onClick={() => setShowAsasFormModal(true)}
            >
              <img src={logoAsasGarantia} alt="Joinsy Logo" />
            </button>
          </div>
        </div>
      </main>

      {/* Modal de Pré-Edital */}
      {/* <PreEditalModal
        open={showPreEditalModal}
        onClose={handleClosePreEditalModal}
        onNavigateToHome={() => {
          scrollToFilters();
          setShowPreEditalModal(false);
        }}
      /> */}

      <AsasFormModal
        open={showAsasFormModal}
        onClose={() => setShowAsasFormModal(false)}
      />
    </HomeContainer>
  );
};
